/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from '../../api/auth';
import type { LoginCredentials } from './types';
import { redirectToLogin } from '../../api/client';
import { notifyStreamLogout } from '../../stream/logoutSignal';

export const CURRENT_USER_QUERY_KEY = ['auth', 'currentUser'];

export function useCurrentUser() {
  return useQuery({
    queryKey: CURRENT_USER_QUERY_KEY,
    queryFn: api.getCurrentUser,
    retry: false,
    staleTime: Infinity,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => api.login(credentials),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CURRENT_USER_QUERY_KEY });
    },
  });
}

export async function logout(): Promise<void> {
  notifyStreamLogout();
  await api.logout();
  redirectToLogin();
}

export const USER_SCOPES_QUERY_KEY = ['auth', 'scopes'];

export function useUserScopes() {
  return useQuery({
    queryKey: USER_SCOPES_QUERY_KEY,
    queryFn: api.getUserScopes,
    retry: false,
    staleTime: Infinity,
  });
}
